import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthService } from 'src/app/service/auth.service';
import { PrivilegedClaimsService } from 'src/app/core/privileged-claims.service';
import { normalizeAppRoles } from 'src/app/core/role-utils';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

type AppRole =
  | 'admin'
  | 'employer'
  | 'employee'
  | 'nurse'
  | 'provider'
  | 'np'
  | 'org_admin'
  | 'clinical_admin'
  | 'super_admin'
  | 'hr'
  | 'scheduler'
  | 'housekeeping'
  | 'laundry'
  | 'lab'
  | 'social-worker'
  | 'finance'
  | 'reception'
  | 'receptionist'
  | 'frontdesk'
  | 'user'
  | 'surveyor'
  | 'don'
  | 'wound_nurse'
  | 'billing_partner'
  | 'credentialing_partner'
  | 'supply_partner'
  | 'patient';

interface RoleClaims {
  requireMfa?: unknown;
  mfaEnrolled?: unknown;
  mfaPassedAt?: unknown;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  email = '';
  password = '';

  loading = false;
  errorMsg: string | null = null;

  private redirecting = false;
  private lastAutoRedirectUid: string | null = null;
  private readonly subscriptions = new Subscription();
  /** Claims from the most recent resolveRoles() call, used by
   *  completeLoginFlow() to route through MFA enroll/challenge before
   *  the normal role-based target when required. */
  private lastClaims: RoleClaims = {};

  constructor(
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private afAuth: AngularFireAuth,
    private privilegedClaims: PrivilegedClaimsService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.route.queryParamMap.subscribe((params) => {
        const reason = params.get('reason');

        if (!reason) {
          return;
        }

        const reasonMessages: Record<string, string> = {
          'admin-role-required':
            'Signed in, but this account does not have access to the admin workspace.',

          'nurse-role-required':
            'Signed in, but this account does not have access to the nurse workspace.',

          'provider-role-required':
            'Signed in, but this account does not have access to the provider workspace.'
        };

        this.errorMsg =
          reasonMessages[reason] ??
          'Signed in, but the redirect was blocked by a guard.';
      })
    );

    this.subscriptions.add(
      this.afAuth.authState.subscribe({
        next: (user) => {
          void this.handleRestoredSession(user);
        },

        error: (error: unknown) => {
          console.error(
            'Firebase authentication state failed:',
            error
          );

          this.errorMsg = this.getErrorMessage(
            error,
            'Unable to restore the authenticated session.'
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async login(): Promise<void> {
    this.errorMsg = null;

    const email = this.email.trim();
    const password = this.password;

    if (!email) {
      this.errorMsg = 'Please enter your email.';
      return;
    }

    if (!password.trim()) {
      this.errorMsg = 'Please enter your password.';
      return;
    }

    this.loading = true;

    try {
      const credential =
        await this.auth.signInWithEmailAndPassword(
          email,
          password
        );

      const user = credential?.user;

      if (!user?.uid) {
        throw new Error(
          'User not found after sign-in.'
        );
      }

      /*
       * AuthService waits for the normalized Firestore user profile.
       * The refreshed token is used only for MFA/session security.
       */
      await this.completeLoginFlow(
        user,
        true
      );
    } catch (error: unknown) {
      console.error(
        'Login error:',
        error
      );

      this.errorMsg = this.getErrorMessage(
        error,
        'Unable to log in with the provided credentials. Please check and try again.'
      );
    } finally {
      this.loading = false;
    }
  }

  private async handleRestoredSession(
    user: firebase.User | null
  ): Promise<void> {
    if (!user?.uid) {
      this.lastAutoRedirectUid = null;
      return;
    }

    if (
      this.loading ||
      this.redirecting ||
      this.lastAutoRedirectUid === user.uid
    ) {
      return;
    }

    const restoringUid = user.uid;

    this.lastAutoRedirectUid =
      restoringUid;

    try {
      /*
       * Restores the normalized profile role used for workspace routing.
       */
      const tokenRoles =
        await this.auth.waitForAnyRole();

      /*
       * The authenticated user may have changed while the profile was loading.
       */
      const currentUser =
        await this.afAuth.currentUser;

      if (
        !currentUser ||
        currentUser.uid !== restoringUid
      ) {
        return;
      }

      if (!tokenRoles.length) {
        throw new Error(
          'No usable application role was found in the user profile.'
        );
      }

      await this.auth.syncClinicalClaimsIfNeeded();

      await this.completeLoginFlow(
        currentUser,
        false
      );
    } catch (error: unknown) {
      console.error(
        'Automatic session restoration failed:',
        error
      );

      if (
        this.lastAutoRedirectUid ===
        restoringUid
      ) {
        this.lastAutoRedirectUid = null;
      }

      this.errorMsg = this.getErrorMessage(
        error,
        'Unable to load this account permissions.'
      );
    }
  }

  private async completeLoginFlow(
    user: firebase.User,
    clearCredentialsOnSuccess: boolean
  ): Promise<void> {
    this.redirecting = true;

    try {
      /*
       * Workspace authorization/routing uses the normalized Firestore profile.
       * MFA remains token-claim based.
       */
      const roles =
        await this.resolveRoles(user);

      const target =
        this.resolveTarget(roles);

      if (!target) {
        throw new Error(
          'No valid application role was found in the user profile.'
        );
      }

      const destination =
        this.resolveMfaRedirect(target);

      const navigated =
        await this.router.navigateByUrl(
          destination
        );

      if (!navigated) {
        throw new Error(
          `Signed in, but navigation to ${destination} was cancelled.`
        );
      }

      if (clearCredentialsOnSuccess) {
        this.email = '';
        this.password = '';
      }
    } finally {
      this.redirecting = false;
    }
  }

  private async resolveRoles(
    user: firebase.User
  ): Promise<string[]> {
    let roles = await this.auth.waitForAnyRole();

    // A claim-only super-admin is intentionally absent from TenantContext.
    // The login gateway may route that privileged identity to the admin
    // boundary without exposing it to operational shells.
    const privileged = await firstValueFrom(this.privilegedClaims.state$.pipe(take(1)));
    if (privileged.isSuperAdmin) roles = ['super_admin'];
    if (!roles.length) {
      if (privileged.isSuperAdmin) roles = ['super_admin'];
      else if (privileged.isOrgAdmin) roles = ['org_admin'];
    }

    // Keep a fresh token only for MFA/session-security claims.
    const tokenResult = await user.getIdTokenResult(true);
    const claims = (tokenResult?.claims ?? {}) as RoleClaims;
    this.lastClaims = {
      requireMfa: claims.requireMfa,
      mfaEnrolled: claims.mfaEnrolled,
      mfaPassedAt: claims.mfaPassedAt,
    };

    console.info('[LOGIN PROFILE ROLES]', {
      uid: user.uid,
      roles,
    });

    if (!roles.length) {
      throw new Error('No normalized role was found in users/{uid}.');
    }

    return normalizeAppRoles(roles);
  }

  private resolveTarget(
    roles: string[]
  ): string | null {
    const normalizedRoles =
      normalizeAppRoles(
        roles
      ) as AppRole[];

    const roleSet =
      new Set<AppRole>(
        normalizedRoles
      );

    if (
      roleSet.has('billing_partner') ||
      roleSet.has('credentialing_partner') ||
      roleSet.has('supply_partner')
    ) {
      return '/partner/dashboard';
    }

    /*
     * Administrative roles have the highest priority.
     */
    if (roleSet.has('super_admin')) return '/super-admin/dashboard';
    if (
      roleSet.has('admin') ||
      roleSet.has('org_admin') ||
      roleSet.has('clinical_admin') ||
      roleSet.has('super_admin')
    ) {
      return '/admin/dashboard';
    }


    /*
     * Clinical roles are evaluated before generic employee
     * roles. Otherwise a user having both "employee" and
     * "nurse" could be sent to the wrong dashboard.
     */
    if (
      roleSet.has('provider') ||
      roleSet.has('np')
    ) {
      return '/provider/dashboard';
    }

    /*
     * The director of nursing reviews and prints finished rounds, so she
     * lands on the reports dashboard. The wound nurse walks the rounds, so
     * she lands on the board.
     */
    if (
      roleSet.has('don')
    ) {
      return '/wound-rounds/dashboard';
    }

    if (
      roleSet.has('wound_nurse')
    ) {
      return '/wound-rounds';
    }

    if (
      roleSet.has('nurse')
    ) {
      return '/nurse/dashboard';
    }

    if (
      roleSet.has('hr') ||
      roleSet.has('scheduler')
    ) {
      return '/scheduling/hr';
    }

    if (
      roleSet.has('lab')
    ) {
      return '/provider/labs';
    }

    if (
      roleSet.has('housekeeping')
    ) {
      return '/housekeeping';
    }

    if (
      roleSet.has('laundry')
    ) {
      return '/laundry';
    }

    if (
      roleSet.has('finance')
    ) {
      return '/finance';
    }

    if (
      roleSet.has('social-worker')
    ) {
      return '/social';
    }

    if (
      roleSet.has('reception') ||
      roleSet.has('receptionist') ||
      roleSet.has('frontdesk') ||
      roleSet.has('employer') ||
      roleSet.has('employee')
    ) {
      return '/patients/dashboard';
    }

    if (
      roleSet.has('surveyor')
    ) {
      return '/surveyor/patients';
    }

    if (
      roleSet.has('patient') &&
      ['jade-dev-5b0e3', 'woundapp-261e6'].includes(environment.firebase.projectId)
    ) {
      return '/telehealth-dev/patient-portal';
    }

    return null;
  }

  /** Routes through MFA enrollment/challenge before the normal
   *  role-based target when the account's claims (see AuthService.
   *  mfaStatus$ / functions/src/mfa.ts) require it. mfaPassedAt is
   *  cleared server-side on every fresh sign-in, so a plain password
   *  login never skips this even for an already-enrolled account. */
  private resolveMfaRedirect(target: string): string {
    const claims = this.lastClaims;
    const requireMfa = claims.requireMfa === true;
    if (!requireMfa) {
      return target;
    }

    const encodedNext = encodeURIComponent(target);

    if (claims.mfaEnrolled !== true) {
      return `/mfa/enroll?next=${encodedNext}`;
    }

    const mfaPassed =
      typeof claims.mfaPassedAt === 'number' && claims.mfaPassedAt > 0;

    if (!mfaPassed) {
      return `/mfa/challenge?next=${encodedNext}`;
    }

    return target;
  }

  private getErrorMessage(
    error: unknown,
    fallback: string
  ): string {
    if (
      error instanceof Error &&
      error.message
    ) {
      return error.message;
    }

    if (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (
        error as {
          message?: unknown;
        }
      ).message === 'string'
    ) {
      return (
        error as {
          message: string;
        }
      ).message;
    }

    return fallback;
  }
}
